import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Download, X } from "lucide-react";
import DataTableCst, {
  DataTableColumn,
  SortDir,
} from "@components/DataTableCst";
import PaginatorCst from "@components/PaginatorCst";
import SearchBar from "@components/SearchableMultiSelectListCst/SearchBar";
import { openToast } from "@utils/toastStore";
import { useRegistryHistory } from "../hooks/useRegistryHistory";
import { RegistryHistoryRow } from "../types";

interface RegistryHistoryModalProps {
  open: boolean;
  onClose: () => void;
  corporateId: string | number;
  editionId: string | number;
}

const HISTORY_PAGE_SIZE = 10;

const formatDate = (value?: number | string | null): string => {
  if (value === null || value === undefined || value === "") return "-";
  const ms = typeof value === "number" && value < 1e12 ? value * 1000 : value;
  return dayjs(ms).format("DD/MM/YYYY");
};

const RegistryHistoryModal: React.FC<RegistryHistoryModalProps> = ({
  open,
  onClose,
  corporateId,
  editionId,
}) => {
  const { t } = useTranslation("edition-detail");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(HISTORY_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    items: rows,
    totalItems,
    totalPages,
    isLoading,
    downloadHistory,
  } = useRegistryHistory({
    corporateId,
    editionId,
    page,
    pageSize,
    search: search || undefined,
    sort: sortField ? `${sortField},${sortDir}` : undefined,
  });

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadHistory();
    } catch {
      openToast({
        severity: "error",
        message: String(t("register.history-modal.download-error")),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const columns: DataTableColumn<RegistryHistoryRow>[] = [
    {
      key: "virtualRegister",
      label: String(t("register.history-modal.columns.virtualRegister")),
      sortable: true,
    },
    {
      key: "uploadDate",
      label: String(t("register.history-modal.columns.uploadDate")),
      sortable: true,
      render: (r) => formatDate(r.uploadDate),
    },
    {
      key: "operatorId",
      label: String(t("register.history-modal.columns.operatorId")),
      sortable: true,
    },
    {
      key: "operatorExternalId",
      label: String(t("register.history-modal.columns.operatorExternalId")),
      sortable: true,
    },
    {
      key: "type",
      label: String(t("register.history-modal.columns.type")),
      sortable: true,
      render: (r) =>
        r.type ? t(`register.history-modal.type.${r.type}`) : "-",
    },
    {
      key: "processingOutcome",
      label: String(t("register.history-modal.columns.processingOutcome")),
      sortable: true,
      render: (r) =>
        r.processingOutcome
          ? t(`register.history-modal.outcome.${r.processingOutcome}`)
          : "-",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("register.history-modal.title")}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "8px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box>
          <Button
            variant="text"
            startIcon={
              isDownloading ? (
                <CircularProgress size={14} />
              ) : (
                <Download size={16} />
              )
            }
            onClick={handleDownload}
            disabled={isDownloading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: theme.palette.primary.main,
              p: 0,
            }}
          >
            {t("register.history-modal.download")}
          </Button>
        </Box>

        <SearchBar
          initialValue={search}
          onSubmit={(value: string) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder={String(t("register.history-modal.search-placeholder"))}
          buttonLabel={String(t("register.history-modal.search-button"))}
        />

        <DataTableCst<RegistryHistoryRow>
          columns={columns}
          rows={rows}
          getRowId={(_, index) => index}
          isLoading={isLoading}
          emptyLabel={String(t("register.history-modal.empty"))}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={handleSortChange}
        />

        {!isLoading && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegistryHistoryModal;
